// src/contexts/CartContext.jsx
import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useUser } from "./UserContext";
import { useLessons } from "./LessonsContext";

const CartContext = createContext();

/* ---------- helpers ---------- */
const normalizeId = (id) => String(id ?? "");

const toDDMMYYYY = (yyyyMmDd) => {
  if (!yyyyMmDd || typeof yyyyMmDd !== "string") return yyyyMmDd || "";
  const [y, m, d] = yyyyMmDd.split("-");
  if (!y || !m || !d) return yyyyMmDd;
  return `${d}-${m}-${y}`;
};

const safeNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const calculateLessonUnits = (startTime, endTime) => {
  if (!startTime || !endTime) return 1;

  try {
    const [sh, sm] = String(startTime).split(":").map(Number);
    const [eh, em] = String(endTime).split(":").map(Number);

    const startMinutes = (sh || 0) * 60 + (sm || 0);
    const endMinutes = (eh || 0) * 60 + (em || 0);

    const diff = endMinutes - startMinutes;
    if (diff <= 0) return 1;

    const units = Math.ceil(diff / 50);
    return units > 0 ? units : 1;
  } catch {
    return 1;
  }
};

const calculateUnitPrice = (totalPrice, units) => {
  const total = safeNumber(totalPrice, 0);
  const amount = Math.max(1, safeNumber(units, 1));
  return amount > 0 ? Number((total / amount).toFixed(2)) : total;
};

/** ✅ Global event name for cart refresh */
const CART_REFRESH_EVENT = "doroos:cart:refresh";

/** ✅ Call this from anywhere (Booking, Lessons, etc.) to force cart refresh */
export const requestCartRefresh = () => {
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event(CART_REFRESH_EVENT));
    }
  } catch {}
};

/* ===== API base (align with other contexts) ===== */
const ENV_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  "";
const CLEAN_ENV_BASE = ENV_BASE.replace(/\/+$/, "");
const FALLBACK_BASE = "https://www.doroosonline.co.il";
const API_BASE = CLEAN_ENV_BASE || FALLBACK_BASE;
const url = (path) => `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;

/* Bearer */
const getBearer = () =>
  localStorage.getItem("jwt") ||
  localStorage.getItem("token") ||
  localStorage.getItem("accessToken") ||
  null;

async function apiGetAuth(path) {
  const token = getBearer();
  if (!token) throw new Error("Missing bearer token");

  const res = await fetch(url(path), {
    method: "GET",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    mode: "cors",
    cache: "no-store",
    credentials: "omit",
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.error || data?.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

async function apiSendAuth(method, path, body) {
  const token = getBearer();
  if (!token) throw new Error("Missing bearer token");

  const res = await fetch(url(path), {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : undefined,
    mode: "cors",
    cache: "no-store",
    credentials: "omit",
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) {
    const err = new Error(data?.error || data?.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

/* ---------- initial state ---------- */
const initialCartState = {
  lessons: [], // [{ id, title, instructor, date(DD-MM-YYYY), time, price(TOTAL), selected, rating?, ratingCount? }]
  recommendedLessons: [],
  expanded: { cart: true, recommendations: true },
  activePayment: "credit",
};

/* ---------- reducer ---------- */
function cartReducer(state, action) {
  switch (action.type) {
    case "SET_LESSONS":
      return { ...state, lessons: action.payload };

    case "CLEAR_LESSONS":
      return { ...state, lessons: [] };

    case "TOGGLE_LESSON_SELECTION": {
      const id = normalizeId(action.payload);
      return {
        ...state,
        lessons: state.lessons.map((l) =>
          normalizeId(l.id) === id ? { ...l, selected: !l.selected } : l
        ),
      };
    }

    case "REMOVE_LESSON": {
      const id = normalizeId(action.payload);
      return {
        ...state,
        lessons: state.lessons.filter((l) => normalizeId(l.id) !== id),
      };
    }

    case "ADD_ITEM": {
      const item = action.payload;
      const id = normalizeId(item?.id);
      if (!id) return state;
      const exists = state.lessons.some((l) => normalizeId(l.id) === id);
      if (exists) return state;
      return { ...state, lessons: [...state.lessons, { ...item, selected: true }] };
    }

    case "TOGGLE_SECTION":
      return {
        ...state,
        expanded: {
          ...state.expanded,
          [action.payload]: !state.expanded[action.payload],
        },
      };

    case "SET_PAYMENT_METHOD":
      return { ...state, activePayment: action.payload };

    default:
      return state;
  }
}

/* ---------- mapping from server lesson -> UI cart item ---------- */
/**
 * ✅ IMPORTANT:
 * price here is the FINAL TOTAL price that already comes from lessons table
 * (no extra calculations).
 */
function mapServerLessonToCartItem(lsn) {
  const instructor =
    [lsn.first_name, lsn.last_name].filter(Boolean).join(" ").trim() || "מורה";

  const priceRaw =
    lsn.price ??
    lsn.lesson_price ??
    lsn.total_price ??
    lsn.session_price ??
    null;

  const finalPrice = safeNumber(priceRaw, 0);

  const teacherRatingRaw =
    lsn.teacher_rating != null && lsn.teacher_rating !== ""
      ? Number(lsn.teacher_rating)
      : lsn.course_rating != null && lsn.course_rating !== ""
      ? Number(lsn.course_rating)
      : NaN;

  const teacherRatingCountRaw =
    lsn.teacher_rating_count ?? lsn.course_rating_count ?? 0;

  const rating =
    Number.isFinite(teacherRatingRaw) && teacherRatingRaw > 0
      ? teacherRatingRaw
      : undefined;

  const ratingCountNumber =
    typeof teacherRatingCountRaw === "number"
      ? teacherRatingCountRaw
      : Number(teacherRatingCountRaw);

  const ratingCount =
    Number.isFinite(ratingCountNumber) && ratingCountNumber > 0
      ? ratingCountNumber
      : undefined;

  const startTime = lsn.start_time?.slice(0, 5) || "";
  const endTime = lsn.end_time?.slice(0, 5) || "";

  const hasRecording =
    Number(lsn.has_recording ?? 0) === 1 ||
    Number(lsn.recording ?? 0) === 1 ||
    Number(lsn.hasRecordings ?? 0) === 1;

  const normalizedRecordings = Array.isArray(lsn.recordings)
    ? lsn.recordings
    : hasRecording
    ? [{ recordingUrl: lsn.recording_url || "HAS_RECORDING" }]
    : [];

  // GEFEN / school-program eligibility: a lesson whose course belongs to a
  // program (ownership_type === 'PROGRAM' or access_scope === 'PROGRAM_ONLY')
  // can only be paid for with that program's wallet credit, never with
  // card/PayPal/general credit.
  const programId =
    lsn.program_id != null && lsn.program_id !== "" ? Number(lsn.program_id) : null;
  const isGefen =
    !!lsn.is_gefen ||
    programId != null ||
    lsn.ownership_type === "PROGRAM" ||
    lsn.access_scope === "PROGRAM_ONLY";

  return {
    id: String(lsn.id),
    title: lsn.title || "שיעור",
    instructor,
    date: toDDMMYYYY(lsn.date),
    time: `${startTime} - ${endTime}`.trim(),

    start_time: startTime,
    end_time: endTime,

    price: finalPrice,

    selected: true,
    rating,
    ratingCount,

    status: String(lsn.status || "").toUpperCase(),
    type: String(lsn.type || "group").toLowerCase(),

    recordingUrl: lsn.recording_url || (hasRecording ? "HAS_RECORDING" : ""),
    hasRecordings: hasRecording,
    recordings: normalizedRecordings,

    paymentDeadlineAt: lsn.payment_deadline_at || null,
    paymentExpired: !!lsn.payment_expired,
    purchased: !!lsn.already_purchased,

    isGefen,
    programId,
    programName: lsn.program_name || null,
    ownershipType: lsn.ownership_type || null,
    accessScope: lsn.access_scope || null,
    gefenWalletId:
      lsn.gefen_wallet_id != null && lsn.gefen_wallet_id !== ""
        ? Number(lsn.gefen_wallet_id)
        : null,
    gefenWalletBalance: safeNumber(lsn.gefen_wallet_balance, 0),
  };
}

/* ---------- payment popup ---------- */
function startCreditPayment({ paymentUrl }) {
  return new Promise((resolve, reject) => {
    if (!paymentUrl) return reject(new Error("Missing paymentUrl"));

    const w = window.open(
      paymentUrl,
      "doroos_payment",
      "width=480,height=720,menubar=no,toolbar=no,location=no,status=no"
    );

    if (!w) {
      return reject(new Error("Popup blocked. Please allow popups."));
    }

    const handleMessage = (event) => {
      const data = event.data || {};
      if (data.action !== "close") return;

      window.removeEventListener("message", handleMessage);

      try {
        if (!w.closed) w.close();
      } catch (e) {}

      if (data.status === "success") resolve({ ok: true, message: data.message });
      else reject(new Error(data.message || "Payment canceled/failed"));
    };

    window.addEventListener("message", handleMessage);
  });
}

/* ---------- context ---------- */
export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialCartState);

  const { user, showAlert, updateLocalCreditBalance } = useUser();
  const isAuthed = !!user?.id;

  const { markLessonsPurchased, canAddToCart } = useLessons();

  const refreshCart = useCallback(async ({ silent = true } = {}) => {
    if (!isAuthed) {
      dispatch({ type: "CLEAR_LESSONS" });
      return { validItems: [], invalidItems: [] };
    }
    try {
      const data = await apiGetAuth("/api/my/cart");
      const items = Array.isArray(data?.items) ? data.items : [];
      const mapped = items.map(mapServerLessonToCartItem);

      const validItems = mapped.filter((item) => canAddToCart(item));
      const invalidItems = mapped.filter((item) => !canAddToCart(item));

      dispatch({ type: "SET_LESSONS", payload: validItems });

      if (invalidItems.length) {
        await Promise.allSettled(
          invalidItems.map((item) =>
            apiSendAuth("DELETE", `/api/my/cart/${encodeURIComponent(item.id)}`)
          )
        );

        if (!silent) {
          showAlert?.(
            "חלק מהשיעורים הוסרו מהעגלה כי הם כבר אינם זמינים לרכישה.",
            "warning"
          );
        }
      }

      return { validItems, invalidItems };
    } catch (e) {
      console.warn("[Cart] refresh failed:", e?.message || e);
      throw e;
    }
  }, [isAuthed, canAddToCart, showAlert]);

  useEffect(() => {
    refreshCart().catch(() => {});
  }, [refreshCart]);

  useEffect(() => {
    const onFocus = () => {
      if (isAuthed) refreshCart().catch(() => {});
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [isAuthed, refreshCart]);

  useEffect(() => {
    const onStorage = (e) => {
      if (["jwt", "token", "accessToken"].includes(e.key)) {
        setTimeout(() => refreshCart().catch(() => {}), 0);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [refreshCart]);

  /** ✅ Listen for cross-app refresh requests (Booking -> Cart, etc.) */
  useEffect(() => {
    const onRefreshEvent = () => refreshCart().catch(() => {});
    window.addEventListener(CART_REFRESH_EVENT, onRefreshEvent);
    return () => window.removeEventListener(CART_REFRESH_EVENT, onRefreshEvent);
  }, [refreshCart]);

  const ENV_TAX =
    (typeof import.meta !== "undefined" &&
      import.meta.env &&
      import.meta.env.VITE_REACT_APP_TAX) ||
    (typeof import.meta !== "undefined" &&
      import.meta.env &&
      import.meta.env.VITE_TAX) ||
    process.env.REACT_APP_TAX ||
    process.env.TAX ||
    "0.17";

  const TAX_RATE = (() => {
    const n = Number(ENV_TAX);
    return Number.isFinite(n) && n >= 0 ? n : 0.17;
  })();

  /* ----- derived totals (no extra lesson calculations) ----- */
  const orderSummary = useMemo(() => {
    const selected = state.lessons.filter((l) => l.selected);
    const gefenSelected = selected.filter((l) => l.isGefen);

    // Wallet-first allocation (client-side mirror of the server logic, for
    // display only): each GEFEN lesson's price is covered from its own
    // program's wallet balance first; whatever is left — including the
    // full price of public lessons — is the subtotal that normal payment
    // methods (card / PayPal / general credit) need to cover.
    const remainingWalletByProgram = {};
    let gefenWalletCovered = 0;

    const subtotal = selected.reduce((sum, l) => {
      const price = safeNumber(l.price, 0);
      let walletPortion = 0;

      if (l.isGefen && l.programId != null) {
        if (!(l.programId in remainingWalletByProgram)) {
          remainingWalletByProgram[l.programId] = safeNumber(l.gefenWalletBalance, 0);
        }
        walletPortion = Math.min(remainingWalletByProgram[l.programId], price);
        remainingWalletByProgram[l.programId] -= walletPortion;
      }

      gefenWalletCovered += walletPortion;
      return sum + Math.max(0, price - walletPortion);
    }, 0);

    const currentCredit = Math.max(
      0,
      Number(user?.credit_balance ?? user?.creditBalance ?? 0)
    );

    const creditUsed = Math.min(currentCredit, subtotal);
    const remainingBeforeVat = Math.max(subtotal - creditUsed, 0);
    const vat = remainingBeforeVat > 0 ? remainingBeforeVat * TAX_RATE : 0;

    const gefenTotal = gefenSelected.reduce((sum, l) => sum + safeNumber(l.price, 0), 0);
    const gefenProgramIds = Array.from(
      new Set(gefenSelected.map((l) => l.programId).filter((id) => id != null))
    );

    return {
      subtotal,
      TAX_RATE,
      creditBalance: currentCredit,
      creditUsed,
      remainingBeforeVat,
      vat,
      total: remainingBeforeVat + vat,
      selectedCount: selected.length,

      // GEFEN-specific breakdown (informational — the server allocates
      // authoritatively when the payment is actually created)
      gefenSelectedCount: gefenSelected.length,
      gefenTotal,
      gefenProgramIds,
      gefenWalletCovered,
      gefenShortfall: Math.max(0, gefenTotal - gefenWalletCovered),
    };
  }, [state.lessons, TAX_RATE, user?.credit_balance, user?.creditBalance]);

  /* ----- public API ----- */
  const isInCart = useCallback(
    (lessonId) => state.lessons.some((l) => normalizeId(l.id) === normalizeId(lessonId)),
    [state.lessons]
  );

  const toggleLessonSelection = (id) =>
    dispatch({ type: "TOGGLE_LESSON_SELECTION", payload: id });

  const removeLesson = async (id) => {
    dispatch({ type: "REMOVE_LESSON", payload: id }); // optimistic
    try {
      await apiSendAuth("DELETE", `/api/my/cart/${encodeURIComponent(id)}`);
    } catch (e) {
      console.warn("[Cart] remove failed:", e?.message || e);
    }
  };

  /**
   * ✅ add lesson from online:
   * if lesson.price is already final total, use it directly (no computeSessionPrice)
   */
  const addLessonFromOnline = async (lesson, course) => {
    if (!lesson) return;
    const lessonId = normalizeId(lesson.id);
    if (!lessonId) return;

    const optimisticPrice =
      lesson?.price != null && lesson?.price !== "" ? Number(lesson.price) : 0;

    const optimistic = {
      id: lessonId,
      title: lesson.title || "שיעור",
      instructor: course?.teacher?.name || "מורה",
      date: toDDMMYYYY(lesson.date),

      time: `${lesson.startTime || lesson.start_time || ""} - ${lesson.endTime || lesson.end_time || ""}`.trim(),

      // ✅ חשוב לקבלה
      start_time: lesson.startTime || lesson.start_time || "",
      end_time: lesson.endTime || lesson.end_time || "",

      // ✅ מחיר המפגש הכולל
      price: Number.isFinite(optimisticPrice) ? optimisticPrice : 0,

      selected: true,
      rating: course?.rating ?? undefined,
      ratingCount: course?.ratingCount ?? undefined,

      status: String(lesson?.status || "").toUpperCase(),
      type: String(lesson?.type || course?.type || "group").toLowerCase(),
      recordingUrl: lesson?.recordingUrl || lesson?.recording_url || "",
      paymentDeadlineAt: lesson?.paymentDeadlineAt || lesson?.payment_deadline_at || null,
      paymentExpired: !!(lesson?.paymentExpired || lesson?.payment_expired),
      purchased: !!lesson?.purchased,
    };

    let hadOptimisticInsert = false;
    if (!isInCart(lessonId)) {
      dispatch({ type: "ADD_ITEM", payload: optimistic });
      hadOptimisticInsert = true;
    }

    try {
      const data = await apiSendAuth("POST", "/api/my/cart", {
        lessonId: lessonId,
        lesson_id: Number(lessonId),
        courseId: course?.id ?? undefined,
        course_id: course?.id ? Number(course.id) : undefined,
      });

      const items = Array.isArray(data?.items) ? data.items : [];
      const mapped = items.map(mapServerLessonToCartItem);
      dispatch({ type: "SET_LESSONS", payload: mapped });
    } catch (e) {
      console.warn("[Cart] add failed:", e?.message || e);
      if (hadOptimisticInsert) {
        dispatch({ type: "REMOVE_LESSON", payload: lessonId });
      }
      showAlert?.(e?.message || "נכשל להוסיף את השיעור לעגלה", "error");
    }
  };


  const validateCheckoutSelection = useCallback(async (lessonsToValidate) => {
    const payloadLessons = (Array.isArray(lessonsToValidate) ? lessonsToValidate : [])
      .filter((l) => l?.id != null)
      .map((l) => ({ id: Number(l.id) }));

    if (!payloadLessons.length) {
      return {
        ok: false,
        cartChanged: false,
        message: "לא נבחרו שיעורים לתשלום",
        invalid: [],
        validLessonIds: [],
      };
    }

    try {
      return await apiSendAuth("POST", "/api/my/cart/checkout", {
        lessons: payloadLessons,
        lessonIds: payloadLessons.map((l) => l.id),
      });
    } catch (e) {
      if (e?.status === 409) {
        return {
          ok: false,
          cartChanged: true,
          ...(e.data || {}),
        };
      }
      throw e;
    }
  }, []);

  /* ---------- checkout ---------- */

  function startPopupPayment({ paymentUrl, popupName = "doroos_payment" }) {
    return new Promise((resolve, reject) => {
      if (!paymentUrl) return reject(new Error("Missing paymentUrl"));

      const w = window.open(
        paymentUrl,
        popupName,
        "width=520,height=760,menubar=no,toolbar=no,location=no,status=no"
      );

      if (!w) {
        return reject(new Error("Popup blocked. Please allow popups."));
      }

      const handleMessage = (event) => {
        const data = event.data || {};
        if (data.action !== "close") return;

        window.removeEventListener("message", handleMessage);

        try {
          if (!w.closed) w.close();
        } catch {}

        if (data.status === "success") {
          resolve({ ok: true, message: data.message, payload: data.payload || null });
        } else {
          reject(new Error(data.message || "Payment canceled/failed"));
        }
      };

      window.addEventListener("message", handleMessage);
    });
  }
  
  const handleCheckout = async (paymentMethodOrEvent, options = {}) => {
    let paymentMethod = paymentMethodOrEvent;

    if (paymentMethodOrEvent && typeof paymentMethodOrEvent === "object") {
      if (typeof paymentMethodOrEvent.preventDefault === "function") {
        paymentMethodOrEvent.preventDefault();
      }
      paymentMethod = state.activePayment || "credit";
    }

    if (!paymentMethod || typeof paymentMethod !== "string") {
      paymentMethod = "credit";
    }

    const selectedLessons = Array.isArray(options.lessons)
      ? options.lessons
      : state.lessons.filter((l) => l.selected);

    if (selectedLessons.length === 0) {
      throw new Error("לא נבחרו שיעורים לתשלום");
    }

    const lessonIds = selectedLessons.map((l) => l.id);

    // GEFEN-program lessons are covered first from that program's own
    // wallet balance; only the shortfall (or the full price, for public
    // lessons, or when the wallet can't cover everything) is charged
    // through the chosen normal payment method below. The server
    // re-fetches each lesson and does this allocation authoritatively —
    // these client-side numbers are only used to show the student what to
    // expect before they pay.
    const gefenInSelection = selectedLessons.filter((l) => l.isGefen);

    // Client-side mirror of the server's wallet-first allocation, used only
    // to show the student an accurate total before they pay — the server
    // re-fetches prices/balances and is the source of truth.
    const remainingWalletByProgram = {};
    let walletCoveredTotal = 0;
    const shortfallByLessonId = {};

    selectedLessons.forEach((l) => {
      const price = safeNumber(l.price, 0);
      let walletPortion = 0;

      if (l.isGefen && l.programId != null) {
        if (!(l.programId in remainingWalletByProgram)) {
          remainingWalletByProgram[l.programId] = safeNumber(l.gefenWalletBalance, 0);
        }
        walletPortion = Math.min(remainingWalletByProgram[l.programId], price);
        remainingWalletByProgram[l.programId] -= walletPortion;
      }

      walletCoveredTotal += walletPortion;
      shortfallByLessonId[l.id] = Math.max(0, price - walletPortion);
    });

    const subtotal = Object.values(shortfallByLessonId).reduce((sum, v) => sum + v, 0);

    const currentCredit = Math.max(
      0,
      Number(user?.credit_balance ?? user?.creditBalance ?? 0)
    );

    const creditUsed = Math.min(currentCredit, subtotal);
    const taxableSubtotal = Math.max(subtotal - creditUsed, 0);
    const vat = taxableSubtotal > 0 ? taxableSubtotal * TAX_RATE : 0;
    const total = taxableSubtotal + vat;

    const totals = {
      subtotal,
      credit_used: creditUsed,
      taxable_subtotal: taxableSubtotal,
      vat,
      tax_rate: TAX_RATE,
      total,
      currency: "ILS",
      gefen_wallet_used: walletCoveredTotal,
    };

    const receiptDtls = {
      user_id: user?.id,
      customer_id: user?.id,
      customer_name:
        [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
        user?.name ||
        "",
      customer_email: user?.email || "",
      customer_phone: user?.phone || user?.mobile || "",
      credit_balance_before: currentCredit,
      credit_used: creditUsed,
      totals,
      lessons: selectedLessons.map((l) => {
        const amount = calculateLessonUnits(l.start_time, l.end_time);
        const totalPrice = safeNumber(l.price, 0);
        const unitPrice = calculateUnitPrice(totalPrice, amount);

        return {
          id: l.id,
          lesson_id: l.id,
          title: l.title || "שיעור",
          date: l.date || "",
          start_time: l.start_time || "",  
          end_time: l.end_time || "",
          time: l.time || "",
          price: totalPrice,
          unit_price: unitPrice,
          amount,
          total: totalPrice,
          teacher: l.instructor || "מורה",
          teacher_name: l.instructor || "מורה",
        };
      }),
    };

    try {
      if (paymentMethod === "credit") {
        const createRes = await apiSendAuth(
          "POST",
          "/api/my/cart/payments/credit/create-process",
          {
            lessonIds,
            lessons: selectedLessons.map((l) => ({
              id: l.id,
              price: safeNumber(l.price, 0),
            })),
            totals,
            receiptDtls,
            email: receiptDtls.customer_email,
            mobile: receiptDtls.customer_phone,
            use_credit: true,
          }
        );

        if (createRes?.fullCredit === true) {
          markLessonsPurchased(lessonIds);
          await refreshCart().catch(() => {});

          if (typeof updateLocalCreditBalance === "function") {
            const remaining =
              createRes?.credit_balance_after ??
              createRes?.remainingCredit ??
              createRes?.creditBalance ??
              createRes?.user?.credit_balance;

            if (remaining != null) {
              updateLocalCreditBalance(Number(remaining));
            }
          }

          showAlert?.("הרכישה הושלמה בהצלחה באמצעות הקרדיט הקיים", "success");
          return { success: true, lessonIds };
        }

        const paymentUrl =
          createRes?.paymentUrl ||
          createRes?.provider?.paymentUrl ||
          createRes?.provider?.data?.paymentUrl ||
          createRes?.provider?.url ||
          createRes?.provider?.data?.url;

        if (!paymentUrl) {
          throw new Error("Missing paymentUrl from server response");
        }

        await startPopupPayment({
          paymentUrl,
          popupName: "doroos_credit_payment",
        });

        markLessonsPurchased(lessonIds);
        await refreshCart().catch(() => {});

        if (typeof updateLocalCreditBalance === "function") {
          const remaining =
            createRes?.credit_balance_after ??
            createRes?.remainingCredit ??
            createRes?.creditBalance ??
            createRes?.user?.credit_balance;

          if (remaining != null) {
            updateLocalCreditBalance(Number(remaining));
          }
        }

        showAlert?.("התשלום בוצע בהצלחה וההרשמה לשיעורים עודכנה במערכת", "success");
        return { success: true, lessonIds };
      }

      if (paymentMethod === "paypal") {
        const createRes = await apiSendAuth(
          "POST",
          "/api/my/cart/payments/paypal/create-order",
          {
            lessonIds,
            lessons: selectedLessons.map((l) => ({
              id: l.id,
              price: safeNumber(l.price, 0),
            })),
            totals,
            receiptDtls,
            email: receiptDtls.customer_email,
            mobile: receiptDtls.customer_phone,
            use_credit: true,
          }
        );

        if (createRes?.fullCredit === true) {
          markLessonsPurchased(lessonIds);
          await refreshCart().catch(() => {});

          if (typeof updateLocalCreditBalance === "function") {
            const remaining =
              createRes?.credit_balance_after ??
              createRes?.remainingCredit ??
              createRes?.creditBalance ??
              createRes?.user?.credit_balance;

            if (remaining != null) {
              updateLocalCreditBalance(Number(remaining));
            }
          }

          showAlert?.("הרכישה הושלמה בהצלחה באמצעות הקרדיט הקיים", "success");
          return { success: true, lessonIds };
        }

        const paymentUrl = createRes?.approvalUrl || createRes?.paymentUrl;
        if (!paymentUrl) {
          throw new Error("Missing PayPal approvalUrl");
        }

        const popupResult = await startPopupPayment({
          paymentUrl,
          popupName: "doroos_paypal_payment",
        });

        markLessonsPurchased(lessonIds);
        await refreshCart().catch(() => {});

        if (typeof updateLocalCreditBalance === "function") {
          const remainingCredit = popupResult?.payload?.remainingCredit;
          if (remainingCredit != null) {
            updateLocalCreditBalance(Number(remainingCredit));
          }
        }

        showAlert?.("תשלום PayPal הושלם בהצלחה", "success");
        return { success: true, lessonIds };
      }

      throw new Error("Unsupported payment method");
    } catch (e) {
      console.error("[Cart] checkout error:", e);
      showAlert?.(e?.message || "שגיאה בתהליך התשלום", "error");
      throw e;
    }
  };

  const toggleSection = (section) =>
    dispatch({ type: "TOGGLE_SECTION", payload: section });

  const setPaymentMethod = (method) =>
    dispatch({ type: "SET_PAYMENT_METHOD", payload: method });

  const value = {
    lessons: state.lessons,
    recommendedLessons: state.recommendedLessons,
    expanded: state.expanded,
    activePayment: state.activePayment,
    orderSummary,

    isInCart,
    toggleLessonSelection,
    removeLesson,
    addLessonFromOnline,

    // deprecated
    addToCart: (id) =>
      console.warn("Deprecated: addToCart by id (use addLessonFromOnline)"),

    toggleSection,
    setPaymentMethod,
    handleCheckout,
    validateCheckoutSelection,

    refreshCart,

    // ✅ export the event-based refresh as well (optional)
    requestCartRefresh,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
};